import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { FileText, Loader2, ShieldCheck, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const MOTIFS = [
  { id: 'medical', label: 'Raison médicale' },
  { id: 'demenagement', label: 'Déménagement' },
  { id: 'autre', label: 'Autre' },
];

export default function ResiliationRequestCard() {
  const [eng, setEng] = useState(null);
  const [requests, setRequests] = useState([]);
  const [motif, setMotif] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [e, r] = await Promise.all([
        axios.get(`${API}/engagement/me`, { withCredentials: true }),
        axios.get(`${API}/resiliation/my`, { withCredentials: true }),
      ]);
      setEng(e.data);
      setRequests(r.data.items || []);
    } catch (err) { /* silencieux */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Visible uniquement si la cliente a un abonnement en cours
  if (!eng || !['mandat_signe', 'active', 'impayee'].includes(eng.subscription_status)) return null;

  const submit = async () => {
    if (!motif) { toast.error('Merci de sélectionner un motif.'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('motif', motif);
      fd.append('message', message);
      if (file) fd.append('file', file);
      await axios.post(`${API}/resiliation/request`, fd, { withCredentials: true });
      toast.success('Demande de résiliation envoyée au studio.');
      setMotif(''); setMessage(''); setFile(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de l\'envoi de la demande.');
    } finally { setSubmitting(false); }
  };

  const pending = eng.has_pending_request;

  return (
    <div className="mt-10 max-w-xl mx-auto" data-testid="resiliation-card">
      <Card className="bg-white border-[#E9DDCC] rounded-2xl">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#C99A55]" strokeWidth={1.8} />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#C99A55] font-semibold">Mon engagement</span>
          </div>
          {eng.engaged && eng.engagement_end_date && (
            <p className="text-sm text-[#7A6455]">
              Vous êtes engagée jusqu'au <strong>{new Date(eng.engagement_end_date).toLocaleDateString('fr-FR')}</strong> (12 mois la 1re année). La résiliation en libre-service n'est pas possible pendant cette période : déposez une demande, le studio la validera.
            </p>
          )}

          {pending ? (
            <div className="flex items-center gap-2 bg-[#FFF8E6] border border-[#C99A55]/40 rounded-xl p-3 text-sm text-[#493329]" data-testid="resiliation-pending">
              <Clock className="w-4 h-4 text-[#9A744C]" /> Votre demande de résiliation est en cours de traitement par le studio.
            </div>
          ) : (
            <div className="space-y-3">
              <h3 className="font-heading text-base text-[#493329]">Demande de résiliation</h3>
              <div>
                <label className="text-xs text-[#7A6455] font-medium">Motif</label>
                <select value={motif} onChange={(e) => setMotif(e.target.value)}
                  className="mt-1 h-9 w-full rounded-lg border border-[#E9DDCC] bg-white px-2.5 text-sm text-[#493329] focus:outline-none focus:ring-2 focus:ring-[#C99A55]"
                  data-testid="resiliation-motif">
                  <option value="">— Sélectionnez —</option>
                  {MOTIFS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#7A6455] font-medium">Message (facultatif)</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                  className="mt-1 w-full rounded-lg border border-[#E9DDCC] bg-white px-2.5 py-2 text-sm text-[#493329] focus:outline-none focus:ring-2 focus:ring-[#C99A55]"
                  data-testid="resiliation-message" />
              </div>
              <div>
                <label className="text-xs text-[#7A6455] font-medium flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Pièce justificative (PDF ou image, facultatif)</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="mt-1 w-full text-sm text-[#493329] file:mr-3 file:rounded-full file:border-0 file:bg-[#F5EFE5] file:px-3 file:py-1.5 file:text-[#9A744C]"
                  data-testid="resiliation-file" />
              </div>
              <Button onClick={submit} disabled={submitting || !motif}
                className="w-full rounded-full h-11 bg-[#493329] hover:bg-[#2f2019] text-white disabled:opacity-50"
                data-testid="resiliation-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer ma demande'}
              </Button>
            </div>
          )}

          {requests.length > 0 && (
            <div className="pt-2 border-t border-[#EFE4D0]">
              <div className="text-[10px] uppercase tracking-wider text-[#AE9885] mb-1.5">Historique</div>
              <ul className="space-y-1 text-xs text-[#7A6455]">
                {requests.map((r) => (
                  <li key={r.id} className="flex justify-between">
                    <span>{MOTIFS.find((m) => m.id === r.motif)?.label || r.motif} · {new Date(r.created_at).toLocaleDateString('fr-FR')}</span>
                    <span className={r.status === 'approved' ? 'text-emerald-700' : r.status === 'refused' ? 'text-[#C76542]' : 'text-[#9A744C]'}>
                      {r.status === 'pending' ? 'En attente' : r.status === 'approved' ? 'Acceptée' : 'Refusée'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
